'use client';

import { useSelectedVariant, type PdpVariant } from '@/lib/variant-store';
import { formatCentsClient, preorderLine } from '@/lib/format';

interface Props {
  variants:       PdpVariant[];
  /** Strikethrough list price for single-variant products with a real compare-at. */
  compareAtCents: number | null;
}

// Price for the chosen variant; before a pick on multi-variant products it
// shows the range. Under it: low-stock and pre-order notes, from live data.
export function VariantPrice({ variants, compareAtCents }: Props) {
  const r = useSelectedVariant(variants);
  const prices = variants.map((v) => v.priceCents);
  const lo = Math.min(...prices);
  const hi = Math.max(...prices);
  const price = r.priceCents != null
    ? formatCentsClient(r.priceCents)
    : lo === hi ? formatCentsClient(lo) : `${formatCentsClient(lo)} – ${formatCentsClient(hi)}`;
  const showCompare = compareAtCents && !r.hasVariants && r.priceCents != null && compareAtCents > r.priceCents;

  // Mixed variant: some units in stock, more open for pre-order. Say which is which.
  const mixed = r.variant && r.inStock && r.variant.preorder && r.variant.available > 0 ? r.variant : null;
  const low = r.variant && r.inStock && !r.variant.preorder && r.variant.available > 0 && r.variant.available <= 3 ? r.variant.available : null;

  return (
    <div>
      <div className="flex items-baseline gap-4">
        <span className="font-numerals text-2xl text-ink">{price}</span>
        {showCompare && (
          <span className="font-numerals text-base text-muted-strong line-through">{formatCentsClient(compareAtCents!)}</span>
        )}
      </div>
      {r.isPreorder && (
        <p className="mt-3 text-sm text-ink/75">{preorderLine(r.shipsBy)}</p>
      )}
      {mixed && (
        <p className="mt-3 text-sm text-ink/75">
          {mixed.available} in stock now. Any quantity beyond that is a pre-order. {preorderLine(mixed.preorder!.shipsBy)}
        </p>
      )}
      {low && (
        <p className="mt-3 text-xs uppercase tracking-[0.1em] text-ink/70">
          {low === 1 ? 'Only 1 left' : `Only ${low} left`}
        </p>
      )}
    </div>
  );
}
